import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./TeamSocialExperiencePage.css";
import { ArrowLeft } from "lucide-react";
import progressIcon from "../../../assets/progressIcon6.png";
import sliderDogHead from "../../../assets/dogHead.png";
import nextIcon from "../../../assets/nextButtonIcon2.png";
import dogLevel0 from "../../../assets/dogLevel5.png";
import dogLevel1 from "../../../assets/dogLevel1.png";
import dogLevel2 from "../../../assets/dogLevel2.png";
import dogLevel3 from "../../../assets/dogLevel3.png";
import dogLevel4 from "../../../assets/dogLevel4.png";
import socialPawsLogo from "../../../assets/socialPawsLogo.svg";
import MobileTopBar from "../../../components/MobileTopBar";
import MobileBottomBar from "../../../components/MobileBottomBar";
import DesktopTopBar from "../../../components/DesktopTopBar";
import DesktopBottomBar from "../../../components/DesktopBottomBar";
import progressMobileIcon from "../../../assets/progressIcon3.png";

const TeamSocialExperiencePage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const isDraggingRef = useRef(false);

  const TRACK_START = 5;
  const TRACK_END = 95;
  const TRACK_RANGE = TRACK_END - TRACK_START;
  const SLIDER_STEPS = 4; // steps 0 to 4 (5 steps)

  const experienceLevels = [
    { value: 0, label: "does petting count?" },
    { value: 1, label: "have walked" },
    { value: 2, label: "have boarded" },
    { value: 3, label: "have fostered" },
    { value: 4, label: "grew up with or owned" },
  ];

  const getInitialValue = () => {
    if (
      formData.pastExperienceLevelWithDogs &&
      typeof formData.pastExperienceLevelWithDogs === "object"
    ) {
      return formData.pastExperienceLevelWithDogs.value;
    }
    const strVal =
      formData.pastExperienceWithDogs ||
      formData.past_experience_with_dogs ||
      (typeof formData.pastExperienceLevelWithDogs === "string"
        ? formData.pastExperienceLevelWithDogs
        : null);

    if (typeof strVal === "string") {
      const foundIdx = experienceLevels.findIndex(
        (lvl) => lvl.label.toLowerCase() === strVal.toLowerCase(),
      );
      if (foundIdx !== -1) return foundIdx;
    }
    return 1;
  };

  const [sliderValue, setSliderValue] = useState(getInitialValue);

  useEffect(() => {
    setSliderValue(getInitialValue());
  }, [formData]);

  const handleSliderChange = (value) => {
    const clamped = Math.max(0, Math.min(SLIDER_STEPS, value));
    setSliderValue(clamped);
  };

  const handleNext = () => {
    const selectedObj = experienceLevels[sliderValue];
    setFormData((prev) => ({
      ...prev,
      pastExperienceLevelWithDogs: {
        value: sliderValue,
        label: selectedObj.label,
      },
      pastExperienceWithDogs: selectedObj.label,
      past_experience_with_dogs: selectedObj.label,
    }));
    navigate("/team-social-dimsum-availability");
  };

  const handleSkip = () => {
    navigate("/team-social-dimsum-availability");
  };

  const dogLevelImages = [
    dogLevel0, // level 0
    dogLevel1, // level 1
    dogLevel2, // level 2
    dogLevel3, // level 3
    dogLevel4, // level 4
  ];

  const currentDogLevelImage = dogLevelImages[sliderValue] || dogLevel0;

  /* DRAG LOGIC - Supports both Horizontal (Desktop) and Vertical (Mobile) */
  const updateValueFromPosition = (clientX, clientY) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth <= 1023;

    let percent;
    if (isMobile) {
      let y = clientY - rect.top;
      y = Math.max(0, Math.min(rect.height, y));
      percent = 1 - y / rect.height;
    } else {
      let x = clientX - rect.left;
      x = Math.max(0, Math.min(rect.width, x));
      percent = x / rect.width;
    }

    const steppedValue = Math.round(percent * SLIDER_STEPS);
    handleSliderChange(steppedValue);
  };

  const handlePointerDown = (e) => {
    e.stopPropagation();
    isDraggingRef.current = true;
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    if (clientX !== undefined && clientY !== undefined) {
      updateValueFromPosition(clientX, clientY);
    }
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;
    if (e.cancelable) {
      e.preventDefault();
    }
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    updateValueFromPosition(clientX, clientY);
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
  };

  const handleTrackClick = (e) => {
    const clientX = e.clientX;
    const clientY = e.clientY;
    if (clientX !== undefined && clientY !== undefined) {
      updateValueFromPosition(clientX, clientY);
    }
  };

  useEffect(() => {
    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", stopDragging);
    window.addEventListener("touchmove", handlePointerMove, { passive: false });
    window.addEventListener("touchend", stopDragging);

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", stopDragging);
    };
  }, []);

  const valPosPercent = `${TRACK_START + (sliderValue / SLIDER_STEPS) * TRACK_RANGE}%`;

  return (
    <div className="social-exp-container">
      <MobileTopBar
        onBack={() => {
          navigate("/dog-social-personality", { state: { step: 1 } });
        }}
        backColor="#000"
        logoColor="#009fd9"
        blueLogo="true"
        isProtectedRoute={false}
      />

      <DesktopTopBar
        showBack={true}
        onBack={() => navigate("/dog-social-personality")}
        backColor="#000"
        showProgress={true}
        progressIconSrc={progressIcon}
        progressPercent={100}
        iconLeft={95}
        iconWidth="80px"
      />

      {/* Main Content */}
      <div className="social-exp-content">
        <div className="tsp-header-section tsp-mobile-only">
          <div className="sep-dog-icon-container">
            <img
              src={progressMobileIcon}
              alt="Progress Step"
              className="tsp-progress-icon"
            />
          </div>
          <div className="sep-progress-track">
            <div className="sep-progress-fill sep-step-1-fill"></div>
          </div>
        </div>

        <h1 className="social-exp-title">
          WHAT IS YOUR PAST EXPERIENCE WITH DOGS?
        </h1>
        <p className="social-exp-subtitle">Honesty is the best Pawlicy!</p>

        {/* Custom Slider Component */}
        <div
          className="custom-slider-container-tse"
          ref={sliderRef}
          onClick={handleTrackClick}
        >
          <div className="slider-track-bg-tse"></div>

          <div
            className="slider-track-fill-tse"
            style={{
              width: valPosPercent,
              "--mobile-fill-height": valPosPercent,
            }}
          ></div>

          <div className="slider-steps-wrapper-ex">
            {[...Array(SLIDER_STEPS + 1)].map((_, index) => {
              const stepPosPercent = `${TRACK_START + (index / SLIDER_STEPS) * TRACK_RANGE}%`;

              return (
                <div
                  key={index}
                  className={`slider-step-item ${index % 2 === 0 ? "left-label" : "right-label"}`}
                  style={{
                    left: stepPosPercent,
                    "--mobile-bottom-pos": stepPosPercent,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSliderChange(index);
                  }}
                >
                  <div className="slider-dot-tse"></div>

                  <span className="slider-label-tse">
                    {experienceLevels[index]?.label}
                  </span>
                </div>
              );
            })}
          </div>

          <div
            className="slider-thumb-dog"
            style={{
              left:
                window.innerWidth <= 1023
                  ? sliderValue === 3
                    ? "49%"
                    : sliderValue === 4
                      ? "49%"
                      : "50%"
                  : valPosPercent,

              "--mobile-bottom-pos": valPosPercent,
            }}
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
          >
            <img
              src={currentDogLevelImage}
              alt={`DogIcon`}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              className={`sp-dog-level-icon ${
                sliderValue === 3 || sliderValue === 4
                  ? "sp-dog-level-large"
                  : ""
              }`}
            />
          </div>
        </div>
      </div>

      <div className="tse-mobile-footer tse-mobile-only">
        <MobileBottomBar
          onNext={handleNext}
          isNextEnabled={true}
          nextIconSrc={nextIcon}
          showSkip={true}
          isSkipBlue={true}
          onSkip={handleSkip}
          variant="blue"
        />
      </div>

      <DesktopBottomBar
        onNext={handleNext}
        isNextEnabled={true}
        nextIconSrc={nextIcon}
        variant="blue"
      />
    </div>
  );
};

export default TeamSocialExperiencePage;
